import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Checkbox, Col, Form, Input, Row, Select, message } from "antd";
import "../../styles/billing-config-page.style.scss";
import { getActiveOperators, fetchTravelsDetailsForConfig, updateTravelConfig } from "../../actions/admin.action";
import { activeOperatorsSelector } from "../../selector/admin.selector";
import { isValid, isValidArray } from "../../utils/utilities";
import { dafidyUserCheck } from "../../utils/constant";

const { Option } = Select;

const BILLING_CATEGORY = "Billing";

export const BillingConfigPage = () => {

    const dispatch = useDispatch();

    const isDafidyUser = dafidyUserCheck();

    const { activeOperators, activeOperatorLoadedTime, configKeys } = useSelector(activeOperatorsSelector);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [selectedOperatorCode, setSelectedOperatorCode] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        const FIVE_MINUTES = 5 * 60 * 1000;
        const shouldFetch = !isValid(activeOperators) ||
                            !activeOperatorLoadedTime ||
                            (Date.now() - activeOperatorLoadedTime) > FIVE_MINUTES;
        if (shouldFetch) {
            dispatch(getActiveOperators());
        }
        if (!isDafidyUser) {
            loadConfig(global.operator_code);
        }
    }, []);

    const loadConfig = (operatorCode) => {
        fetchTravelsDetailsForConfig(operatorCode, (data) => {
            setSelectedOperator(data);
        }, (errmsg) => {
            message.error(errmsg);
        });
    };

    const onChangeOperator = (operatorCode) => {
        setSelectedOperatorCode(operatorCode);
        form.resetFields();
        if (isValid(operatorCode)) {
            loadConfig(operatorCode);
        } else {
            setSelectedOperator(null);
        }
    };

    const billingConfigKeys = useMemo(() => {
        if (!isValidArray(configKeys)) {
            return [];
        }
        return configKeys.filter((configKey) => configKey.category === BILLING_CATEGORY);
    }, [configKeys]);

    useEffect(() => {
        if (isValidArray(billingConfigKeys) && isValid(selectedOperator) && isValid(selectedOperator.travels_app_config)) {
            const configValues = {};
            billingConfigKeys.forEach((configKey) => {
                const value = selectedOperator.travels_app_config[configKey.key_name];
                if (configKey.is_boolean) {
                    configValues[configKey.key_name] = value == 1;
                } else if (configKey.is_multiple_options && isValid(value)) {
                    configValues[configKey.key_name] = value.split(',').map(v => v.trim());
                } else {
                    configValues[configKey.key_name] = value;
                }
            });
            form.setFieldsValue(configValues);
        }
    }, [billingConfigKeys, selectedOperator]);

    const renderFormItem = (configKey) => {
        const { key_name, key_description, is_boolean, key_options, is_multiple_options, placeholder } = configKey;
        const label = isValid(key_description) ? key_description : key_name;
        return (
            <Col span={8} key={key_name}>
                <Form.Item
                    label={label}
                    name={key_name}
                    rules={[{ required: false }]}
                    valuePropName={is_boolean ? "checked" : "value"}
                >
                    {is_boolean ? (
                        <Checkbox />
                    ) : isValid(key_options) ? (
                        <Select
                            mode={is_multiple_options ? "multiple" : undefined}
                            options={key_options.split(',').map(opt => ({
                                label: opt.trim(),
                                value: opt.trim()
                            }))}
                            style={{ minWidth: '120px' }}
                            placeholder={placeholder || `Select ${label}`}
                        />
                    ) : (
                        <Input placeholder={placeholder || `Enter ${label}`} />
                    )}
                </Form.Item>
            </Col>
        );
    };

    const onFinish = (values) => {
        if (!isValid(selectedOperator)) {
            message.error("Operator details not loaded yet");
            return;
        }
        const configValues = {};
        Object.entries(values).forEach(([key, value]) => {
            configValues[key] = value === true ? 1 : value === false ? 0 : value;
        });

        const operatorRegion = selectedOperator.region;

        if (operatorRegion && operatorRegion.toLowerCase() === "north") {
            dispatch(updateTravelConfig(
                selectedOperator.operator_code, operatorRegion,
                { travels_app_config: configValues },
                () => {
                    message.success("Updated successfully.");
                },
                (errmsg) => {
                    message.error(errmsg);
                }
            ));
        }

        dispatch(updateTravelConfig(
            selectedOperator.operator_code, "south",
            { travels_app_config: configValues },
            () => {
                message.success("Updated successfully.");
            },
            (errmsg) => {
                message.error(errmsg);
            }
        ));
    };

    const checkboxItems = billingConfigKeys.filter(configKey => configKey.is_boolean).map(renderFormItem);
    const otherItems = billingConfigKeys.filter(configKey => !configKey.is_boolean).map(renderFormItem);

    const showConfigSection = !isDafidyUser || isValid(selectedOperatorCode);

    return (
        <div className="billing-config-page">
            <div className="billing-config-card">
                <h2 className="billing-config-title">Billing Config</h2>
                {isDafidyUser &&
                    <Row gutter={[16, 16]} style={{ padding: '10px 0' }}>
                        <Col>
                            <Select
                                showSearch
                                allowClear
                                placeholder="Select a travel"
                                style={{ minWidth: 240 }}
                                value={selectedOperatorCode}
                                onChange={onChangeOperator}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.children ?? '').toLowerCase().includes((input || '').toLowerCase())
                                }
                            >
                                {isValidArray(activeOperators) && activeOperators.map((operator) => (
                                    <Option value={operator.operator_code} key={operator.operator_code}>
                                        {operator.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>
                }
                {showConfigSection &&
                <Form
                    form={form}
                    onFinish={onFinish}
                    autoComplete="off"
                >
                    {isValidArray(billingConfigKeys) ? (
                        <Row gutter={[16, 16]}>
                            {checkboxItems}
                            {otherItems}
                        </Row>
                    ) : (
                        <div className="billing-config-empty">No billing configuration entries found.</div>
                    )}
                    <Row justify="center" style={{ marginTop: 12 }}>
                        <Col>
                            <Button type="primary" htmlType="submit" disabled={!isValidArray(billingConfigKeys)}>
                                Update
                            </Button>
                        </Col>
                    </Row>
                </Form>
                }
            </div>
        </div>
    );
};
